import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, makeStateKey, StateKey, TransferState } from '@angular/core';
import {
	ApiActionType,
	ApiPathType,
	DefaultApiActionType,
	IApiErrorDataResponse,
	IApiResponse,
	IDeleteItemsRequest,
	IGetMultipleItemsRequest,
	IListOwnedAndGlobalResponse
} from 'api/api-models.js';
import { PdfApi } from 'api/pdf/pdf-api.js';
import { AppDataService } from 'lib/app-data.service.js';
import { AuthService } from 'lib/auth/auth.service.js';
import { CosmosDb } from 'lib/cosmos/cosmosDb.js';
import { DietDb } from 'lib/cosmos/diet/dietDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { WorkoutDb } from 'lib/cosmos/workout/workoutDb.js';
import { AppErrorBehavior } from 'lib/error/error.models.js';
import { angularErrorHelper } from 'lib/helpers/angular-error-helper.class.js';
import { ImageClientCompression as ClientImageCompression, filesHelper } from 'lib/helpers/files-helper.js';
import { pdfHelper } from 'lib/helpers/pdf-helper.class.js';
import { ImageType } from 'lib/image/image-service.js';
import { PlatformService } from 'lib/platform/index.js';
import { catchError, from, map, Observable, of, RetryConfig, switchMap } from 'rxjs';
import { match, P } from 'ts-pattern';
import { environment } from '../environments/environment.js';
import { backoffRetry } from './retry-strategy.js';

type CallResult<TBodyData> = {
	readonly url: string;
	readonly data?: TBodyData;
	readonly file?: File;
	readonly imageType?: ImageType;
	readonly clientImageCompression?: ClientImageCompression;
};

type QueryStringParam = {
	readonly name: string;
	readonly value: string | undefined;
};

export type HttpServiceErrorType = '404' | 'error' | '401' | '400';

export class HttpServiceError extends Error {
	public readonly errorType: HttpServiceErrorType;
	public readonly originalMessage: string;
	public readonly behavior: AppErrorBehavior | undefined;

	constructor(data: {
		readonly message: string;
		readonly url: string;
		readonly statusCode: number;
		readonly type: HttpServiceErrorType;
		readonly behavior: AppErrorBehavior | undefined;
	}) {
		super(
			`Error executing '${data.url}' with status code '${data.statusCode}'. Error type '${data.type}'. ${data.message}. Behavior '${data.behavior}'`
		);

		this.errorType = data.type;
		this.originalMessage = data.message;
		this.behavior = data.behavior;
	}
}

export class ApiCallBuilder<TBodyData> {
	private type?: ApiPathType;
	private action?: ApiActionType;
	private id?: string;
	private path?: string;
	private data?: TBodyData;
	private file?: File;
	private imageType?: ImageType;
	private readonly queryStrings: QueryStringParam[] = [];

	constructor(private config: { requireData: boolean; requireFile: boolean; platformService: PlatformService }) {}

	withApiRoute(type: ApiPathType, action: DefaultApiActionType): this {
		this.type = type;
		this.action = action;
		return this;
	}

	withCustomApiRoute(type: ApiPathType, action: ApiActionType): this {
		this.type = type;
		this.action = action;
		return this;
	}

	withId(id?: string): this {
		this.id = id;
		return this;
	}

	withPath(path: string): this {
		if (path.startsWith('/')) {
			this.path = path.substring(1, path.length);
		} else {
			this.path = path;
		}
		return this;
	}

	withQueryString(name: string, value: string | number | undefined | boolean): this {
		this.queryStrings.push({
			name: name,
			value: value?.toString()
		});

		return this;
	}

	withData(data: TBodyData): this {
		this.data = data;
		return this;
	}

	withFile(file: File): this {
		this.file = file;
		return this;
	}

	withImageType(imageType: ImageType | undefined): this {
		this.imageType = imageType;
		return this;
	}

	build(): CallResult<TBodyData> {
		if (!this.type) {
			throw Error(`Missing api call type`);
		}
		if (!this.action) {
			throw Error(`Missing api call action`);
		}
		if (this.config.requireData && !this.data) {
			throw Error(`Missing query data`);
		}
		if (this.config.requireFile && !this.file) {
			throw Error(`Missing query file`);
		}
		return {
			url: this.getApiPath(this.type, this.action, this.path, this.id, this.queryStrings),
			data: this.data,
			file: this.file,
			imageType: this.imageType
		};
	}

	private getApiPath(
		type: string,
		action: ApiActionType,
		path?: string,
		id?: string,
		queryStrings?: QueryStringParam[]
	): string {
		let fullPath: string = `api/${type}/${action}`;
		if (id) {
			fullPath = `${fullPath}/${id}`;
		}
		if (path) {
			fullPath = `${fullPath}/${path}`;
		}

		if (queryStrings && queryStrings.length) {
			let separator = '?';
			for (const queryString of queryStrings) {
				fullPath += `${separator}${queryString.name}=${queryString.value}`;
				separator = '&';
			}
		}

		return this.toAbsoluteUrl(fullPath);
	}

	private toAbsoluteUrl(url: string): string {
		if (url.toLowerCase().includes('http')) {
			return url;
		}

		let baseUrl: string | undefined;

		if (this.config.platformService.isBrowser) {
			const origin = location.origin.toLowerCase();

			baseUrl = origin;
			// for (const browserBaseUrl of environment.api.browserBaseUrls) {
			// 	if (origin.includes(browserBaseUrl.toLowerCase())) {
			// 		baseUrl = browserBaseUrl;
			// 	}
			// }
		} else {
			baseUrl = environment.api.serverBaseUrl;
		}

		if (!baseUrl) {
			throw Error(`Origin '${origin}' is not defined within allowed browser base urls`);
		}

		return `${baseUrl}/${url}`;
	}
}

@Injectable({
	providedIn: 'root'
})
export class HttpService {
	private readonly retryConfig: RetryConfig = {
		count: 2,
		delay: 100
	};

	private canUseTransferState: boolean = true;

	constructor(
		private readonly httpClient: HttpClient,
		private readonly authService: AuthService,
		private readonly platformService: PlatformService,
		private readonly transferState: TransferState,
		private readonly appDataService: AppDataService
	) {}

	/**
	 * To ensure that requests are executed after initial response, clear the state
	 * when app is stable
	 */
	disableTransferState(): void {
		this.canUseTransferState = false;
	}

	get<TResponse>(call: (builder: ApiCallBuilder<void>) => CallResult<void>): Observable<IApiResponse<TResponse>> {
		const result = call(
			new ApiCallBuilder<void>({
				requireData: false,
				requireFile: false,
				platformService: this.platformService
			})
		);

		const transferStateKeyForUrl = this.getTransferStateKeyForUrl<IApiResponse<TResponse>>(result.url);

		if (this.platformService.isBrowser && this.canUseTransferState) {
			if (this.transferState.hasKey(transferStateKeyForUrl)) {
				const apiResult = this.transferState.get(transferStateKeyForUrl, null);

				if (apiResult) {
					// we have cached response, return it immediately without making request
					return of(apiResult);
				}
			}
		}

		return this.httpClient
			.get(result.url, {
				headers: {
					Authorization: `Bearer ${this.getAccessToken()}`
				}
			})
			.pipe(
				backoffRetry(result.url, this.retryConfig),
				map((response) => {
					// store in transfer state
					if (this.platformService.isServer) {
						this.transferState.set(transferStateKeyForUrl, response);
					}

					return response as IApiResponse<TResponse>;
				}),
				catchError((error) => {
					this.handleError(result.url, error);
				})
			);
	}

	post<TResponse, TData>(
		call: (builder: ApiCallBuilder<TData>) => CallResult<TData>
	): Observable<IApiResponse<TResponse>> {
		const result = call(
			new ApiCallBuilder<TData>({
				requireData: true,
				requireFile: false,
				platformService: this.platformService
			})
		);

		return this.httpClient
			.post(result.url, this.mapDataToJson(result.data), {
				headers: {
					Authorization: `Bearer ${this.getAccessToken()}`
				}
			})
			.pipe(
				backoffRetry(result.url, this.retryConfig),
				map((response) => {
					return response as IApiResponse<TResponse>;
				}),
				catchError((error) => {
					this.handleError(result.url, error);
				})
			);
	}

	upsert<TCosmosItem extends CosmosDb.ICosmosItem, TBaseModel extends CosmosDb.IBaseItem>(data: {
		type: ApiPathType;
		existingItem: TCosmosItem | undefined;
		patchModel: (item: TCosmosItem) => CosmosDb.PatchItem<TCosmosItem>;
		createModel: TBaseModel;
	}): Observable<IApiResponse<TCosmosItem>> {
		const formData = this.getUpsertFormData<TCosmosItem, TBaseModel>(data);

		return this.post<TCosmosItem, TBaseModel | CosmosDb.PatchItem<TCosmosItem>>((builder) => {
			return builder.withApiRoute(data.type, 'upsert').withData(formData).build();
		});
	}

	create<TCosmosItem extends CosmosDb.ICosmosItem, TBaseModel>(data: {
		type: ApiPathType;
		createModel: TBaseModel;
	}): Observable<IApiResponse<TCosmosItem>> {
		const formData = data.createModel;

		return this.post<TCosmosItem, TBaseModel>((builder) => {
			return builder.withApiRoute(data.type, 'create').withData(formData).build();
		});
	}

	patch<TCosmosItem extends CosmosDb.ICosmosItem>(data: {
		type: ApiPathType;
		patchModel: CosmosDb.PatchItem<TCosmosItem>;
	}): Observable<IApiResponse<TCosmosItem>> {
		const formData = data.patchModel;
		return this.post<TCosmosItem, CosmosDb.PatchItem<TCosmosItem>>((builder) => {
			return builder.withApiRoute(data.type, 'patch').withData(formData).build();
		});
	}

	getSingle<TCosmosItem extends CosmosDb.ICosmosItem>(
		type: ApiPathType,
		id: string
	): Observable<IApiResponse<TCosmosItem>> {
		return this.get<TCosmosItem>((builder) => {
			return builder.withApiRoute(type, 'getSingle').withId(id).build();
		});
	}

	getMultiple<TCosmosItem extends CosmosDb.ICosmosItem>(
		type: ApiPathType,
		ids: string[]
	): Observable<IApiResponse<TCosmosItem>> {
		return this.post<TCosmosItem, IGetMultipleItemsRequest>((builder) => {
			return builder
				.withApiRoute(type, 'getMultiple')
				.withData({
					ids: ids
				})
				.build();
		});
	}

	deleteSingle<TCosmosItem extends CosmosDb.ICosmosItem>(
		type: ApiPathType,
		id: string
	): Observable<IApiResponse<TCosmosItem>> {
		return this.delete<TCosmosItem>((builder) => {
			return builder.withApiRoute(type, 'deleteSingle').withId(id).build();
		});
	}

	deleteMultiple<TCosmosItem extends CosmosDb.ICosmosItem>(
		type: ApiPathType,
		ids: string[]
	): Observable<IApiResponse<TCosmosItem[]>> {
		return this.delete<TCosmosItem[], IDeleteItemsRequest>((builder) => {
			return builder
				.withApiRoute(type, 'deleteMultiple')
				.withData({
					ids: ids
				})
				.build();
		});
	}

	list<TCosmosItem extends CosmosDb.ICosmosItem>(type: ApiPathType): Observable<IApiResponse<TCosmosItem[]>> {
		return this.get<TCosmosItem[]>((builder) => {
			return builder.withApiRoute(type, 'list').build();
		});
	}

	listCustom<TCosmosItem extends CosmosDb.ICosmosItem>(
		type: ApiPathType,
		action: string
	): Observable<IApiResponse<TCosmosItem[]>> {
		return this.get<TCosmosItem[]>((builder) => {
			return builder.withCustomApiRoute(type, action).build();
		});
	}

	listOwned<TCosmosItem extends CosmosDb.ICosmosItem>(type: ApiPathType): Observable<IApiResponse<TCosmosItem[]>> {
		return this.get<TCosmosItem[]>((builder) => {
			return builder.withApiRoute(type, 'listOwned').build();
		});
	}

	listGlobal<TCosmosItem extends CosmosDb.ICosmosItem>(type: ApiPathType): Observable<IApiResponse<TCosmosItem[]>> {
		return this.get<TCosmosItem[]>((builder) => {
			return builder.withApiRoute(type, 'listGlobal').build();
		});
	}

	listGlobalAndOwned<TCosmosItem extends CosmosDb.ICosmosItem>(
		type: ApiPathType
	): Observable<IApiResponse<IListOwnedAndGlobalResponse<TCosmosItem>>> {
		return this.get<IListOwnedAndGlobalResponse<TCosmosItem>>((builder) => {
			return builder.withApiRoute(type, 'listGlobalAndOwned').build();
		});
	}

	delete<TResponse, TBodyData = any>(
		call: (builder: ApiCallBuilder<TBodyData>) => CallResult<TBodyData>
	): Observable<IApiResponse<TResponse>> {
		const result = call(
			new ApiCallBuilder<TBodyData>({
				requireData: false,
				requireFile: false,
				platformService: this.platformService
			})
		);

		return this.httpClient
			.delete(result.url, {
				body: this.mapDataToJson(result.data),
				headers: {
					Authorization: `Bearer ${this.getAccessToken()}`
				}
			})
			.pipe(
				backoffRetry(result.url, this.retryConfig),
				map((response) => {
					return response as IApiResponse<TResponse>;
				}),
				catchError((error) => {
					this.handleError(result.url, error);
				})
			);
	}

	generateTrainingPlanPdf(data: { user: UserDb.IUserItem }): Observable<PdfApi.GeneratePdfResponse> {
		const pdfUrl = this.getPdfSourceUrl({
			type: 'trainingPlan',
			itemId: data.user.id,
			containerItemId: undefined,
			userId: undefined
		});

		return from(pdfHelper.getPdfHashAsync(data.user)).pipe(
			switchMap((pdfHash) => {
				return this.post<PdfApi.GeneratePdfResponse, PdfApi.GeneratePdfRequest>((builder) =>
					builder
						.withCustomApiRoute('pdf', 'generate')
						.withData({
							filename: pdfHelper.getPdfWorkoutOrDietPlanName('training_plan', data.user),
							url: pdfUrl,
							pdfHash: pdfHash
						})
						.build()
				).pipe(
					map((response) => {
						return response.data;
					})
				);
			})
		);
	}

	generateDietPlanPdf(data: { user: UserDb.IUserItem }): Observable<PdfApi.GeneratePdfResponse> {
		const pdfUrl = this.getPdfSourceUrl({
			type: 'dietPlan',
			itemId: data.user.id,
			containerItemId: undefined,
			userId: undefined
		});

		return from(pdfHelper.getPdfHashAsync(data.user)).pipe(
			switchMap((pdfHash) => {
				return this.post<PdfApi.GeneratePdfResponse, PdfApi.GeneratePdfRequest>((builder) =>
					builder
						.withCustomApiRoute('pdf', 'generate')
						.withData({
							filename: pdfHelper.getPdfWorkoutOrDietPlanName('diet_plan', data.user),
							url: pdfUrl,
							pdfHash: pdfHash
						})
						.build()
				).pipe(
					map((response) => {
						return response.data;
					})
				);
			})
		);
	}

	generateWorkoutPdf(data: {
		workout: WorkoutDb.IWorkoutItem;
		userId: string | undefined;
		containerItemId: string | undefined;
	}): Observable<PdfApi.GeneratePdfResponse> {
		const pdfUrl = this.getPdfSourceUrl({
			type: 'workout',
			itemId: data.workout.id,
			userId: data.userId,
			containerItemId: data.containerItemId
		});

		return from(pdfHelper.getPdfHashAsync(data.workout)).pipe(
			switchMap((pdfHash) => {
				return this.post<PdfApi.GeneratePdfResponse, PdfApi.GeneratePdfRequest>((builder) =>
					builder
						.withCustomApiRoute('pdf', 'generate')
						.withData({
							filename: pdfHelper.getPdfFilename(data.workout),
							url: pdfUrl,
							pdfHash: pdfHash
						})
						.build()
				).pipe(
					map((response) => {
						return response.data;
					})
				);
			})
		);
	}

	generateDietPdf(data: {
		diet: DietDb.IDietItem;
		userId: string | undefined;
		containerItemId: string | undefined;
	}): Observable<PdfApi.GeneratePdfResponse> {
		const pdfUrl = this.getPdfSourceUrl({
			type: 'diet',
			itemId: data.diet.id,
			userId: data.userId,
			containerItemId: data.containerItemId
		});

		return from(pdfHelper.getPdfHashAsync(data.diet)).pipe(
			switchMap((pdfHash) => {
				return this.post<PdfApi.GeneratePdfResponse, PdfApi.GeneratePdfRequest>((builder) =>
					builder
						.withCustomApiRoute('pdf', 'generate')
						.withData({
							filename: pdfHelper.getPdfFilename(data.diet),
							url: pdfUrl,
							pdfHash: pdfHash
						})
						.build()
				).pipe(
					map((response) => {
						return response.data;
					})
				);
			})
		);
	}

	uploadFile(data: {
		type: ApiPathType;
		file: File;
		imageType?: ImageType;
		clientImageCompression?: ClientImageCompression;
	}): Observable<IApiResponse<CosmosDb.ICosmosFileReference>> {
		return this.uploadCustomFile<CosmosDb.ICosmosFileReference>((builder) =>
			builder
				.withCustomApiRoute(data.type, 'uploadFile')
				.withFile(data.file)
				.withImageType(data.imageType)
				.build()
		).pipe(
			map((response) => {
				if (response.data.image) {
					// update app data service with image
					const imageGalleries = this.appDataService.imageGalleries();

					if (imageGalleries) {
						imageGalleries.userGallery.userUploadedImages.push(response.data);

						this.appDataService.imageGalleries.set(imageGalleries);
					}
				}

				return response;
			})
		);
	}

	uploadCustomFile<TResponse>(
		call: (builder: ApiCallBuilder<void>) => CallResult<void>
	): Observable<IApiResponse<TResponse>> {
		const result = call(
			new ApiCallBuilder<void>({
				requireData: false,
				requireFile: true,
				platformService: this.platformService
			})
		);

		if (!result.file) {
			throw Error(`Missing file`);
		}

		const formData: FormData = new FormData();

		const sourceFiles: File[] = [result.file];

		// needs to match the 'IUploadFileRequest' interface
		const useImageType: ImageType = result.imageType ?? 'default';
		formData.append('imageType', useImageType);

		return from(filesHelper.compressFilesAsync(sourceFiles, result.clientImageCompression ?? 'default')).pipe(
			switchMap((compressedFiles) => {
				for (let i = 0; i < compressedFiles.length; i++) {
					formData.append(i.toString(), compressedFiles[i], compressedFiles[i].name);
				}

				const accessToken = this.getAccessToken();

				return this.httpClient
					.post(result.url, formData, {
						headers: {
							Authorization: `Bearer ${accessToken}`
						}
					})
					.pipe(
						backoffRetry(result.url, this.retryConfig),
						map((response) => {
							return response as IApiResponse<TResponse>;
						}),
						catchError((error) => {
							this.handleError(result.url, error);
						})
					);
			})
		);
	}

	/**
	 * There is a mismatch between the front-end domain and the server that is running angular
	 * Because of this mismatch the client (browser) is making a request to server even though
	 * the response was already serialized by server and injected into HTML.
	 * With this transform we get the proper key and manually check the existence of the cached response
	 */
	private getTransferStateKeyForUrl<T>(url: string): StateKey<T> {
		// only use for our own urls
		const domainWithoutSubdomains = new URL(environment.productionUrl).hostname.split('.').pop() ?? '____';
		if (url.toLowerCase().includes('localhost') || url.toLowerCase().includes(domainWithoutSubdomains)) {
			const newUrl = new URL(url);
			return makeStateKey<T>(`${newUrl.pathname}/${newUrl.search}`);
		}

		return makeStateKey<T>(url);
	}

	private getPdfSourceUrl(data: {
		type: 'workout' | 'diet' | 'trainingPlan' | 'dietPlan';
		itemId: string;
		userId?: string;
		containerItemId?: string;
	}): string {
		let url: string = `${environment.app.baseUrl}/pdf/${data.type}/${data.itemId}`;

		if (data.userId && data.containerItemId) {
			url += `/${data.userId}/${data.containerItemId}`;
		}

		return url;
	}

	/**
	 * Converts 'undefined' values to 'null'.
	 * This is needed for saving form data because "undefined" fields (e.g. workout notes) would not be updated to empty value
	 * because the property would not be present in data
	 */
	private mapDataToJson(data: any): any {
		const json = JSON.stringify(data, (k, v) => (v === undefined ? null : v));

		return JSON.parse(json);
	}

	private getAccessToken(): string | undefined {
		return this.authService.getAccessToken();
	}

	private getUpsertFormData<TCosmosItem extends CosmosDb.ICosmosItem, TBaseModel extends CosmosDb.IBaseItem>(data: {
		existingItem: TCosmosItem | undefined;
		patchModel: (item: TCosmosItem) => CosmosDb.PatchItem<TCosmosItem>;
		createModel: TBaseModel;
	}): TBaseModel | CosmosDb.PatchItem<TCosmosItem> {
		if (data.existingItem) {
			// patch operation because item already exists
			const model: TCosmosItem = {
				...data.existingItem,
				...data.patchModel(data.existingItem)
			};

			return model;
		}

		const model: TBaseModel & CosmosDb.ICosmosOptionalId = {
			id: undefined,
			...data.createModel
		};

		return model;
	}

	private handleError(url: string, error: any): never {
		const { type, statusCode, behavior, message } = match(error)
			.returnType<{
				readonly type: HttpServiceErrorType;
				readonly statusCode: number;
				readonly behavior: AppErrorBehavior | undefined;
				readonly message: string;
			}>()
			.with(P.instanceOf(HttpErrorResponse), (m) => {
				const data = this.extractDataFromHttpErrorResponse(m);

				return {
					type: data.type,
					statusCode: data.statusCode,
					behavior: data.behavior,
					message: data.message ?? angularErrorHelper.extractErrorData(error).message
				};
			})
			.otherwise(() => {
				return {
					type: 'error',
					statusCode: 0,
					behavior: undefined,
					message: angularErrorHelper.extractErrorData(error).message
				};
			});

		throw new HttpServiceError({
			message: message,
			url: url,
			type: type,
			statusCode: statusCode,
			behavior: behavior
		});
	}

	private extractDataFromHttpErrorResponse(error: HttpErrorResponse): {
		readonly behavior: AppErrorBehavior | undefined;
		readonly type: HttpServiceErrorType;
		readonly message: string | undefined;
		readonly statusCode: number;
	} {
		const type: HttpServiceErrorType = match(error.status)
			.returnType<HttpServiceErrorType>()
			.with(404, () => '404')
			.with(401, () => '401')
			.with(400, () => '400')
			.otherwise(() => 'error');

		let behavior: AppErrorBehavior | undefined = undefined;
		let message: string | undefined = undefined;

		const dataToParse = error.error;
		try {
			const parsedData = dataToParse as IApiErrorDataResponse;

			// check existence of behavior response
			if (parsedData?.behavior) {
				behavior = parsedData.behavior;
			}
			if (parsedData?.message) {
				message = parsedData.message;
			}
		} catch (parsingError) {
			console.warn(`Failed to parse error data: `, dataToParse);
			console.warn('Parsing error: ', parsingError);
		}

		return {
			behavior: behavior,
			statusCode: error.status,
			type: type,
			message: message
		};
	}
}
